import PropTypes from 'prop-types';
import React from 'react';
import { reduxForm, propTypes } from 'redux-form';
import compose from 'recompose/compose';
import { get } from 'lodash';
import Joi from 'joi-full';
import Textarea from '../../../common/textarea';
import Select from '../../../common/select';
import SaveButton from '../../../common/buttons/saveButton';
import { generateTimes } from '../../../common/helpers/schedules';
import RecurringBlockTime from './recurringBlockTime';
import DateTime from '../../../common/DateTime';
import { fullName } from '../../../common/helpers/users';
import { USER_TYPES_LABEL } from '../../../auth/constants';
import { syncValidateData } from '../../../common/validator';
import confirmModal from '../../../common/confirmModal';
import withMinInterval from '../../../common/hoc/withMinInterval';

const BlockTimeForm = ({
  barbers,
  handleSubmit,
  save,
  afterSave,
  hideBarber,
  deleteBlockTime,
  initialValues,
  afterDelete,
  dayOff,
  minInterval,
  isEditing,
}) => {
  const saveAndCallback = data => save(data).then(afterSave);

  const deleteFunc = () => {
    if (initialValues.isRecurring) {
      confirmModal(
        {
          title: '',
          message: 'Would you like to delete all future occurrences of this blocked times?',
          type: 'yesnoreverse',
          confirmButtonText: 'Yes',
          negativeButtonText: 'No',
        },
        () => deleteBlockTime(initialValues.barberId, initialValues.id, true).then(afterDelete),
        () => deleteBlockTime(initialValues.barberId, initialValues.id).then(afterDelete),
        () => null
      );
    } else {
      deleteBlockTime(initialValues.barberId, initialValues.id).then(afterDelete);
    }
  };

  const lastModifiedByKind = get(dayOff, 'lastModifiedBy.kind');
  const createdByKind = get(dayOff, 'createdBy.kind');

  const showModified = get(dayOff, 'createdAt') !== get(dayOff, 'updatedAt');

  return (
    <form
      className="c-inner-wrapper u-pv u-pb0"
      onSubmit={handleSubmit(saveAndCallback)}
    >
      <div className="o-flexbox u--ml-">
        <div className="u-flex-1 u-pl- u-mb-" display-if={!hideBarber}>
          <div className="u-min-width-280">
            <label htmlFor="barberId" className="c-label-new">Barber</label>
            <Select
              name="barberId"
              className="c-select u-1/1"
              options={barbers.map(barber => ({ id: barber.id, name: fullName(barber) }))}
              valueField="id"
              textField="name"
              disabled={!!isEditing}
              data-automation-id="select:barbers"
            />
          </div>
        </div>
        <div className="u-pl- u-nowrap">
          <div className="u-inline-block">
            <label htmlFor="startTime" className="c-label-new">Start time</label>
            <Select
              className="c-select u-pr"
              name="startTime"
              options={generateTimes(true, minInterval)}
              data-automation-id="select:startTime"
            />
          </div>
          <div className="u-inline-block u-pl-">
            <label htmlFor="endTime" className="c-label-new">End time</label>
            <Select
              className="c-select u-pr"
              name="endTime"
              options={generateTimes(true, minInterval)}
              data-automation-id="select:endTime"
            />
          </div>
        </div>
      </div>
      <div>
        <label htmlFor="description" className="c-label-new">Description</label>
        <Textarea
          className="c-textarea"
          name="description"
          data-automation-id="input:description"
        />
      </div>
      <div className="u-mt u-text-right" display-if={dayOff}>
        <div>
          <div className="c-label u-type--normal u-inline-block u-mr--">Created:</div>
          <div className="u-inline-block" display-if={createdByKind}>
            <div className="c-label u-type--normal">
              {USER_TYPES_LABEL[createdByKind]}
              <span className="u-ph--">&ndash;</span>
            </div>
          </div>
          <div className="c-label u-type--normal u-inline-block">
            <DateTime>{dayOff.createdAt}</DateTime>
          </div>
        </div>
        <div display-if={showModified}>
          <div className="c-label u-type--normal u-inline-block u-mr--">modified:</div>
          <div className="u-inline-block" display-if={lastModifiedByKind}>
            <div className="c-label u-type--normal">
              {USER_TYPES_LABEL[lastModifiedByKind]}
              <span className="u-ph--">&ndash;</span>
            </div>
          </div>
          <div className="c-label u-type--normal u-inline-block">
            <DateTime>{dayOff.updatedAt}</DateTime>
          </div>
        </div>
      </div>
      <div className="u--ml u-mt">
        <RecurringBlockTime />
      </div>
      <div className="c-separator u--mh u-mt- medium-and-up-mv- large-and-up-u-mv" />
      <div className="o-flexbox u-pb-">
        <div className="u-1/1-small">
          <button
            display-if={deleteBlockTime}
            type="button"
            className="c-btn c-btn--red u-width-120 u-mr-"
            onClick={deleteFunc}
            data-automation-id="button:delete"
          >
            <span className="c-btn__text">Delete</span>
          </button>
        </div>
        <div className="u-1/1-small">
          <SaveButton content="Save" />
        </div>
      </div>
    </form>
  );
};

BlockTimeForm.propTypes = {
  afterSave: PropTypes.func,
  afterDelete: PropTypes.func,
  barbers: PropTypes.array,
  date: PropTypes.string,
  save: PropTypes.func.isRequired,
  hideBarber: PropTypes.bool,
  deleteBlockTime: PropTypes.func,
  initialValues: PropTypes.object,
  dayOff: PropTypes.object,
  minInterval: PropTypes.number.isRequired,
  isEditing: PropTypes.bool.isRequired,
  ...propTypes,
};

const schema = Joi.object().keys({
  description: Joi.string().max(255).optional().allow(null, ''),
});

export default compose(
  reduxForm({
    form: 'blockTime',
    enableReinitialize: true,
    validate: values => syncValidateData(values, schema),
  }),
  withMinInterval
)(BlockTimeForm);
